import React from 'react';
import PropTypes from 'prop-types';
import anchorme from 'anchorme';
import Link from './shared/Link';
import utils from '../../../common/utils';
import fabric from '../../../common/fabric';
import Quill from '../../../common/quill';

/**
 * Rectangle component
 *
 * @param props
 * @returns {XML}
 * @constructor
 */
const Delta = Quill.import('delta');

const Text = props => {
  const {
    params,
    style,
    groupScaleX,
    groupOpacity,
    matrixGroup,
    hasSymbolAsParent,
  } = props;
  const [svgRendered, setSvgRendered] = React.useState();
  if (!params.height || !params.width) {
    // sometimes, objects get saved with no h or w
    // it's  non-visible on editor but can kill the viewer
    return null;
  }
  // fabric defaut size is 40, which is not saved with includeDefaultValues false
  params.fontSize = params.fontSize || 40;
  params.fontStyle = params.fontStyle || 'normal';
  params.fontFamily = params.fontFamily || 'Times New Roman';
  params.textAlign = params.textAlign || 'left';
  params.lineHeight = params.lineHeight || 1.16;
  if (!params.text && params.contents) {
    const delta = new Delta(params.contents);
    params.text = fabric.LudusText.deltaToText(delta);
  }

  //
  const localScale = 1 / groupScaleX;
  const shadow = params.shadow
    ? {
        ...params.shadow,
        blur: params.shadow.blur * localScale,
        offsetX: params.shadow.offsetX * localScale,
        offsetY: params.shadow.offsetY * localScale,
      }
    : null;
  //
  const groupMatrix = props.matrixGroup
    ? fabric.util.multiplyTransformMatrices(
        matrixGroup,
        params.ludus.transformMatrix,
      )
    : params.ludus.transformMatrix;
  //
  const transformMatrix = fabric.util.multiplyTransformMatrices(
    [props.globalScale, 0, 0, props.globalScale, 0, 0],
    groupMatrix,
  );

  React.useEffect(() => {
    const originalText = params.text;
    const text = params.text.replace('[#]', props.currentSlideNumber);
    const ops = props.params?.contents?.ops;
    const opsArray = ops && Array.isArray(ops) ? ops : Object.values(ops || {});
    opsArray.forEach(op => {
      if (op?.insert) {
        op.insert = op.insert.replace('[#]', props.currentSlideNumber);
      }
    });
    const properties = {
      ...params,
      text,
      fontSize: params.fontSize,
      lineHeight: params.lineHeight || 1.16,
      fontWeight: params.fontWeight,
      fontStyle: params.fontStyle || 'normal',
      textAlign: params.textAlign || 'left',
      fontFamily: params.fontFamily,
      fill: params.fill || 'black',
      charSpacing: params.charSpacing || 0,
      opacity: 1,
      // don't use transform props as it will be applied via matrix
      originX: 'left',
      originY: 'top',
      scaleX: 1,
      scaleY: 1,
      left: 0,
      top: 0,
      angle: 0,
      skewX: 0,
      skewY: 0,
      shadow: null,
    };
    let object;
    const shouldRenderAsSVG =
      !params.renderedAsSVG || hasSymbolAsParent || text !== originalText;
    if (shouldRenderAsSVG) {
      if (params.type === 'ludusText') {
        object = new fabric.LudusText(text, properties);
      } else if (params.type === 'i-text') {
        object = new fabric.IText('', properties);
      }
    }

    // Dynamic height calculation, see comment above
    // const adjustedHeight = object.get('height');
    const _svgRendered = `<svg 
          fill="none" xmlns="http://www.w3.org/2000/svg" height="${
            params.height
          }px" width="${params.width}px">${
      // params.renderedAsSVG ||
      shouldRenderAsSVG ? object.toSVG() : params.renderedAsSVG
    }</svg>`;
    setSvgRendered(
      anchorme(_svgRendered, {
        exclude: urlObj =>
          [
            'http://www.w3.org/1999/xlink',
            'http://www.w3.org/2000/svg',
          ].includes(urlObj.raw),
        attributes: [
          { name: 'target', value: '_blank' },
          { name: 'class', value: 'svg-link' },
          // Use SVG xlink namespace for link attributes to make it work in Safari
          urlObj => ({
            name: 'xlink:href',
            value: urlObj.protocol + urlObj.encoded,
          }),
          () => ({
            name: 'xlink:target',
            value: '_blank',
          }),
        ],
      }),
    );
  }, [props.params]);

  if (!svgRendered) {
    return null;
  }

  const blockStyles = {
    ...style,
    height: params.height,
    width: params.width,
    left: 0,
    top: 0,
    transformOrigin: 'center center',
    opacity: groupOpacity,
    mixBlendMode:
      params.globalCompositeOperation === 'source-over'
        ? 'normal'
        : params.globalCompositeOperation,
    transform: `${
      shadow && utils.isSafari ? 'translateZ(0px)' : ''
    } translate(-50%, -50%) matrix(${transformMatrix})`,

    filter: shadow
      ? utils.getDropShadowCSSFilterFromFabricShadow(shadow, 0, 1, false)
      : 'none',
  };

  return (
    <Link
      hyperlink={props.hyperlink}
      style={{ ...blockStyles, textDecoration: 'none' }}
      dangerouslySetInnerHTML={{ __html: svgRendered }}
    />
  );
};
Text.propTypes = {
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  groupScaleX: PropTypes.number.isRequired,
  groupOpacity: PropTypes.number.isRequired,
  hyperlink: PropTypes.string,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  currentSlideNumber: PropTypes.number,
  hasSymbolAsParent: PropTypes.bool,
  globalScale: PropTypes.number,
};
Text.defaultProps = {
  hyperlink: null,
  currentSlideNumber: 0,
  matrixGroup: null,
  hasSymbolAsParent: false,
  globalScale: 1,
};

export default Text;
